import {
  AccountOrder,
  AccountSection,
  MyAccountVm,
  OrderDetailModalData,
} from './my-account.types';

export function buildFullName(user: any): string {
  const first = user?.fName?.trim() ?? '';
  const last = user?.lName?.trim() ?? '';
  return `${first} ${last}`.trim();
}

export function mapUserVm(user: any): MyAccountVm {
  return {
    user,
    fullName: buildFullName(user),
  };
}

export function isValidAccountSection(
  value: string | null | undefined,
): value is AccountSection {
  return (
    value === 'profile' ||
    value === 'security' ||
    value === 'addresses' ||
    value === 'orders'
  );
}

export function formatCurrency(
  value: string | number | null | undefined,
): string {
  const amount = Number(value);
  if (Number.isNaN(amount)) return '—';

  return new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency: 'GBP',
  }).format(amount);
}

export function formatOrderDate(value: string | null | undefined): string {
  if (!value) return '—';

  const normalized = value.includes('T') ? value : value.replace(' ', 'T');
  const date = new Date(normalized);

  if (Number.isNaN(date.getTime())) return value;

  return new Intl.DateTimeFormat('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(date);
}

export function toTitleCase(value: string | null | undefined): string {
  if (!value) return '—';

  return value
    .replace(/_/g, ' ')
    .toLowerCase()
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

export function mapAddressSummary(addr: any) {
  return {
    id: String(addr?.addressId ?? ''),
    userAddressLinkId: addr?.userAddressLinkId,
    isDefault: Number(addr?.isDefault ?? 0) === 1,
    label: [
      addr?.addressLine1,
      addr?.addressLine2,
      addr?.city,
      addr?.county,
      addr?.postalCode,
    ]
      .filter(Boolean)
      .join(', '),
    line1: addr?.addressLine1 ?? '',
    line2: addr?.addressLine2 ?? '',
    city: addr?.city ?? '',
    county: addr?.county ?? '',
    country: addr?.country ?? '',
    postcode: addr?.postalCode ?? '',
    raw: addr,
  };
}

export function mapAccountOrder(order: any): AccountOrder {
  return {
    id: order?.id,
    orderNumber: order?.publicOrderCode ?? '',
    dateTime: formatOrderDate(order?.createdAtLocal ?? order?.createdAt),
    total: formatCurrency(order?.orderTotalCurrentGross),
    status: toTitleCase(order?.orderStatus ?? ''),
    paymentStatus: toTitleCase(order?.paymentStatus ?? ''),
    fulfilmentType: toTitleCase(order?.orderType ?? ''),
    raw: order,
  };
}

export function mapOrderDetails(
  response: any,
  order: AccountOrder,
): OrderDetailModalData | null {
  const rawItems = Array.isArray(response?.orderDetails)
    ? response.orderDetails
    : [];

  if (!rawItems.length) return null;

  return {
    id: order.id,
    orderNumber: order.orderNumber ?? '',
    status: order.status ?? '',
    paymentStatus: order.paymentStatus ?? '',
    fulfilmentType: order.fulfilmentType ?? '',
    dateTime: order.dateTime ?? '',
    total: order.total ?? '—',
    subtotal: '',
    adjustmentReason: '',
    customerNote: null,
    tenantNote: null,
    items: rawItems.map((item: any) => ({
      name: item?.itemName ?? 'Item',
      quantity: Number(item?.itemQuantity ?? 1),
      price: formatCurrency(item?.itemTotalGrossOriginal ?? 0),
      status: toTitleCase(item?.itemStatus ?? ''),
      note: item?.itemNote ?? null,
      tenantComment: item?.itemTenantComment ?? null,
      extras: Array.isArray(item?.extras)
        ? item.extras.map((extra: any) => ({
            label: extra?.extraName ?? 'Extra',
            price:
              extra?.extraPrice != null
                ? formatCurrency(extra.extraPrice)
                : undefined,
            quantity: Number(extra?.extraQuantity ?? 1),
          }))
        : [],
      raw: item,
    })),
    raw: response,
  };
}
